/**
 * ─────────────────────────────────────────────
 * src/services/authService.js
 * ─────────────────────────────────────────────
 *
 * WHY THIS FILE EXISTS:
 * This is the SERVICE LAYER. It is the only place in the entire app
 * that knows HOW to talk to your backend auth API.
 *
 * ANALOGY:
 * Think of it like a waiter in a restaurant.
 * - The kitchen (backend) knows how to cook food
 * - The customer (component) wants food
 * - The waiter (service) carries the order and brings back the food
 *
 * Components should NOT know about API URLs.
 * Redux thunks should NOT know about HTTP methods.
 * Only the SERVICE knows these details.
 *
 * BENEFIT:
 * If tomorrow your backend URL changes from /api/auth/login
 * to /api/v2/auth/login — you change it in ONE place here.
 * Not in 10 different components.
 *
 * WHAT EACH FUNCTION DOES:
 * Each function matches exactly one backend route.
 * It calls axios, gets the response, and returns the data.
 * If axios throws, the error bubbles up to the Redux thunk.
 */

import api from "../lib/api";

// ─────────────────────────────────────────────
// login({ email, password })
// → POST /api/auth/login
// → Returns: { token, user: { _id, name, email, role } }
// ─────────────────────────────────────────────
const login = async (credentials) => {
  // credentials = { email, password }
  // api.post() returns an axios response object
  // response.data is what your backend actually sends back
  // i.e., { success: true, message: "Login successful.", data: { token, user } }
  const response = await api.post("/auth/login", credentials);
  return response.data; // { success, message, data: { token, user } }
};

// ─────────────────────────────────────────────
// forgotPassword({ email })
// → POST /api/auth/forgot-password
// → Returns: { success: true, message: "OTP sent to your email." }
// ─────────────────────────────────────────────
const forgotPassword = async ({ email }) => {
  const response = await api.post("/auth/forgot-password", { email });
  return response.data;
};

// ─────────────────────────────────────────────
// verifyOtp({ email, otp })
// → POST /api/auth/verify-otp
// → Returns: { success: true, message: "OTP verified..." }
// ─────────────────────────────────────────────
const verifyOtp = async ({ email, otp }) => {
  const response = await api.post("/auth/verify-otp", { email, otp });
  // The server requires this single-use token on reset-password, so a reset
  // can only come from the browser that actually verified the OTP.
  const resetToken = response.data?.data?.resetToken;
  if (resetToken) sessionStorage.setItem("resetToken", resetToken);
  return response.data;
};

// ─────────────────────────────────────────────
// resetPassword({ email, password })
// → POST /api/auth/reset-password
// → Returns: { success: true, message: "Password reset successfully." }
// ─────────────────────────────────────────────
const resetPassword = async ({ email, password }) => {
  const resetToken = sessionStorage.getItem("resetToken") || "";
  const response = await api.post("/auth/reset-password", {
    email,
    password,
    resetToken,
  });
  sessionStorage.removeItem("resetToken");
  return response.data;
};

// ─────────────────────────────────────────────
// setPassword({ token, password })
// → POST /api/auth/set-password
// → Used for invite link flow
// → Returns: { success: true, message: "Password set successfully." }
// ─────────────────────────────────────────────
const setPassword = async ({ token, password }) => {
  const response = await api.post("/auth/onboarding/set-password", { token, password });
  return response.data;
};

// ─────────────────────────────────────────────
// Export all service functions as a single object
// Usage in authSlice.js:
//   import authService from '../services/authService'
//   authService.login(credentials)
// ─────────────────────────────────────────────
const authService = {
  login,
  forgotPassword,
  verifyOtp,
  resetPassword,
  setPassword,
};

export default authService;
